import React, { useState, useEffect } from 'react';
import { WordSearchGrid, WordPosition } from '../types/Level';
import { Plus, Trash2, RotateCw, Check, Wand2 } from 'lucide-react';

interface WordSearchGridEditorProps {
  grid: WordSearchGrid | undefined;
  onChange: (grid: WordSearchGrid) => void;
  category: string;
  suggestedWords?: string;
}

const WordSearchGridEditor: React.FC<WordSearchGridEditorProps> = ({ grid, onChange, category, suggestedWords }) => {
  const defaultWidth = 8;
  const defaultHeight = 8;

  const [gridWidth, setGridWidth] = useState(grid?.width || defaultWidth);
  const [gridHeight, setGridHeight] = useState(grid?.height || defaultHeight);
  const [gridData, setGridData] = useState<string[][]>(
    grid?.grid || Array(defaultHeight).fill(null).map(() => Array(defaultWidth).fill(''))
  );
  const [wordPositions, setWordPositions] = useState<WordPosition[]>(grid?.wordPositions || []);
  const [newWord, setNewWord] = useState('');
  const [placementMode, setPlacementMode] = useState(false);
  const [currentWord, setCurrentWord] = useState('');
  const [currentDirection, setCurrentDirection] = useState<WordPosition['direction']>('horizontal-reverse');
  const [selectedCell, setSelectedCell] = useState<{ x: number; y: number } | null>(null);
  const [validPlacements, setValidPlacements] = useState<Set<string>>(new Set());
  const [partialPlacements, setPartialPlacements] = useState<Set<string>>(new Set());
  const [availableDirections, setAvailableDirections] = useState<Set<WordPosition['direction']>>(new Set<WordPosition['direction']>(['horizontal-reverse', 'vertical', 'vertical-up', 'diagonal-down-left', 'diagonal-up-left']));
  const [overlapDirections, setOverlapDirections] = useState<Set<WordPosition['direction']>>(new Set<WordPosition['direction']>());

  // Hebrew letters for random fill
  const hebrewLetters = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ', 'ק', 'ר', 'ש', 'ת'];

  // Helper function to convert Hebrew ending letters to regular letters
  const normalizeHebrewLetter = (letter: string): string => {
    const endingToRegular: { [key: string]: string } = {
      'ן': 'נ', 'ם': 'מ', 'ף': 'פ', 'ץ': 'צ', 'ך': 'כ'
    };
    return endingToRegular[letter] || letter;
  };

  // Helper function to strip spaces, apostrophes, and quotes from words for grid placement
  const stripWordForGrid = (word: string): string => {
    return word.replace(/[\s'"]/g, '');
  };

  // Update parent when grid changes
  useEffect(() => {
    onChange({
      width: gridWidth,
      height: gridHeight,
      grid: gridData,
      wordPositions: wordPositions
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData, wordPositions, gridWidth, gridHeight]);

  const initializeGrid = (width: number, height: number) => {
    const newGrid = Array(height).fill(null).map(() => Array(width).fill(''));
    setGridData(newGrid);
    setGridWidth(width);
    setGridHeight(height);
    setWordPositions([]);
  };

  const handleCellChange = (row: number, col: number, value: string) => {
    const normalized = normalizeHebrewLetter(value.toUpperCase());
    const newGrid = gridData.map((r, i) =>
      i === row ? r.map((c, j) => j === col ? normalized : c) : r
    );
    setGridData(newGrid);
  };

  // Helper function to check if a direction is incorrect for Hebrew
  const isIncorrectDirection = (direction: string): boolean => {
    return direction === 'horizontal' ||
      direction === 'diagonal-down-right' ||
      direction === 'diagonal-up-right';
  };


  const getDirectionVector = (direction: WordPosition['direction']): [number, number] => {
    switch (direction) {
      case 'horizontal-reverse': return [0, -1];
      case 'vertical': return [1, 0];
      case 'vertical-up': return [-1, 0];
      case 'diagonal-down-left': return [1, -1];
      case 'diagonal-up-left': return [-1, -1];
      default: return [0, -1];
    }
  };

  const canPlaceWord = (word: string, startX: number, startY: number, direction: WordPosition['direction']): { canPlace: boolean; hasOverlap: boolean } => {
    const [dx, dy] = getDirectionVector(direction);
    const strippedWord = stripWordForGrid(word);
    const normalizedWord = strippedWord.split('').map(normalizeHebrewLetter).join('');
    let hasOverlap = false;

    for (let i = 0; i < normalizedWord.length; i++) {
      const x = startX + (dx * i);
      const y = startY + (dy * i);

      if (x < 0 || x >= gridHeight || y < 0 || y >= gridWidth) {
        return { canPlace: false, hasOverlap: false };
      }

      const existingLetter = gridData[x][y];
      if (existingLetter) {
        // Check if this cell is part of any placed word
        let isPartOfPlacedWord = false;
        for (const wordPos of wordPositions) {
          const [wpDx, wpDy] = getDirectionVector(wordPos.direction);
          const wpStrippedWord = stripWordForGrid(wordPos.word);
          const wpNormalizedWord = wpStrippedWord.split('').map(normalizeHebrewLetter).join('');

          for (let j = 0; j < wpNormalizedWord.length; j++) {
            const wpX = wordPos.startX + (wpDx * j);
            const wpY = wordPos.startY + (wpDy * j);

            if (wpX === x && wpY === y) {
              isPartOfPlacedWord = true;
              // If it's a placed word letter, it must match
              if (existingLetter !== normalizedWord[i]) {
                return { canPlace: false, hasOverlap: false };
              }
              hasOverlap = true;
              break;
            }
          }
          if (isPartOfPlacedWord) break;
        }
        // If the letter is NOT part of a placed word, treat it as empty (can be overridden)
      }
    }

    return { canPlace: true, hasOverlap };
  };

  const calculateAvailableDirections = (word: string): { available: Set<WordPosition['direction']>, overlaps: Set<WordPosition['direction']> } => {
    const allDirections: WordPosition['direction'][] = [
      'horizontal-reverse', 'vertical', 'vertical-up',
      'diagonal-down-left', 'diagonal-up-left'
    ];
    const available = new Set<WordPosition['direction']>();
    const overlaps = new Set<WordPosition['direction']>();

    for (const direction of allDirections) {
      // Check if word can be placed anywhere with this direction
      let hasOverlapPlacement = false;
      let foundAvailable = false;

      for (let x = 0; x < gridHeight && !foundAvailable; x++) {
        for (let y = 0; y < gridWidth && !foundAvailable; y++) {
          const result = canPlaceWord(word, x, y, direction);
          if (result.canPlace) {
            available.add(direction);
            foundAvailable = true; // Early exit once we find one valid placement
            if (result.hasOverlap) {
              hasOverlapPlacement = true;
            }
          }
        }
      }
      if (hasOverlapPlacement) {
        overlaps.add(direction);
      }
    }

    return { available, overlaps };
  };

  const calculateValidPlacements = (word: string, direction: WordPosition['direction']) => {
    const valid = new Set<string>();
    const partial = new Set<string>();

    for (let x = 0; x < gridHeight; x++) {
      for (let y = 0; y < gridWidth; y++) {
        const result = canPlaceWord(word, x, y, direction);
        if (result.canPlace) {
          const key = `${x}-${y}`;
          if (result.hasOverlap) {
            partial.add(key);
          } else {
            valid.add(key);
          }
        }
      }
    }

    setValidPlacements(valid);
    setPartialPlacements(partial);
  };

  const placeWord = (word: string, startX: number, startY: number, direction: WordPosition['direction']) => {
    const result = canPlaceWord(word, startX, startY, direction);
    if (!result.canPlace) {
      alert('Cannot place word here - insufficient space or conflicting letters!');
      return;
    }

    const [dx, dy] = getDirectionVector(direction);
    const strippedWord = stripWordForGrid(word);
    const normalizedWord = strippedWord.split('').map(normalizeHebrewLetter).join('');
    const newGrid = gridData.map(row => [...row]);

    for (let i = 0; i < normalizedWord.length; i++) {
      const x = startX + (dx * i);
      const y = startY + (dy * i);
      newGrid[x][y] = normalizedWord[i];
    }

    setGridData(newGrid);
    setWordPositions([...wordPositions, {
      word: word,
      startX: startX,
      startY: startY,
      direction: direction
    }]);
    setPlacementMode(false);
    setCurrentWord('');
    setSelectedCell(null);
    setValidPlacements(new Set());
    setPartialPlacements(new Set());

    // Randomize direction for next word placement (weighted: less chance for diagonals)
    const directions: WordPosition['direction'][] = [
      'horizontal-reverse', 'horizontal-reverse', 'horizontal-reverse', 'horizontal-reverse',
      'vertical', 'vertical', 'vertical-up', 'vertical-up',
      'diagonal-down-left', 'diagonal-up-left'
    ];
    const randomDirection = directions[Math.floor(Math.random() * directions.length)];
    setCurrentDirection(randomDirection);
  };

  const handleCellClick = (row: number, col: number) => {
    if (placementMode && currentWord) {
      placeWord(currentWord, row, col, currentDirection);
    } else {
      setSelectedCell({ x: row, y: col });
    }
  };

  const removeWord = (index: number) => {
    const wordPos = wordPositions[index];
    const [dx, dy] = getDirectionVector(wordPos.direction);
    const strippedWord = stripWordForGrid(wordPos.word);
    const normalizedWord = strippedWord.split('').map(normalizeHebrewLetter).join('');
    const newGrid = gridData.map(row => [...row]);

    // Get all other word positions (excluding the one being removed)
    const otherWordPositions = wordPositions.filter((_, i) => i !== index);

    // Clear the word from the grid, but preserve shared letters
    for (let i = 0; i < normalizedWord.length; i++) {
      const x = wordPos.startX + (dx * i);
      const y = wordPos.startY + (dy * i);

      // Check if this position is part of any other word
      let isPartOfOtherWord = false;
      for (const otherWordPos of otherWordPositions) {
        const [otherDx, otherDy] = getDirectionVector(otherWordPos.direction);
        const otherStrippedWord = stripWordForGrid(otherWordPos.word);
        const otherNormalizedWord = otherStrippedWord.split('').map(normalizeHebrewLetter).join('');

        for (let j = 0; j < otherNormalizedWord.length; j++) {
          const otherX = otherWordPos.startX + (otherDx * j);
          const otherY = otherWordPos.startY + (otherDy * j);

          if (otherX === x && otherY === y) {
            isPartOfOtherWord = true;
            break;
          }
        }
        if (isPartOfOtherWord) break;
      }

      // Only clear the cell if it's not part of another word
      if (!isPartOfOtherWord) {
        newGrid[x][y] = '';
      }
    }

    setGridData(newGrid);
    setWordPositions(wordPositions.filter((_, i) => i !== index));
  };

  const repositionWord = (index: number) => {
    const wordPos = wordPositions[index];
    // Remove the word from the grid and word positions
    removeWord(index);
    // Set the word for placement mode
    setNewWord(wordPos.word);
    setCurrentWord(wordPos.word);
    setCurrentDirection(wordPos.direction);
    setPlacementMode(true);
    calculateValidPlacements(wordPos.word, wordPos.direction);
  };


  const fillEmptyCells = () => {
    const newGrid = gridData.map(row =>
      row.map(cell => cell || hebrewLetters[Math.floor(Math.random() * hebrewLetters.length)])
    );
    setGridData(newGrid);
  };

  const clearGrid = () => {
    if (window.confirm('Are you sure you want to clear the entire grid? This will remove all words and letters.')) {
      initializeGrid(gridWidth, gridHeight);
    }
  };

  const clearRandomLetters = () => {
    // Create a new grid with only word letters, rest are empty
    const newGrid = gridData.map(row => row.map(() => ''));

    // Re-place all words on the empty grid
    wordPositions.forEach(wordPos => {
      const [dx, dy] = getDirectionVector(wordPos.direction);
      const strippedWord = stripWordForGrid(wordPos.word);
      const normalizedWord = strippedWord.split('').map(normalizeHebrewLetter).join('');

      for (let i = 0; i < normalizedWord.length; i++) {
        const x = wordPos.startX + (dx * i);
        const y = wordPos.startY + (dy * i);
        newGrid[x][y] = normalizedWord[i];
      }
    });

    setGridData(newGrid);
  };

  const startPlacingWord = () => {
    if (!newWord.trim()) {
      alert('Please enter a word first!');
      return;
    }
    const word = newWord.trim();

    // Calculate available directions when user clicks Place Word
    const { available, overlaps } = calculateAvailableDirections(word);
    setAvailableDirections(available);
    setOverlapDirections(overlaps);

    // If current direction is not available, switch to first available
    if (!available.has(currentDirection) && available.size > 0) {
      setCurrentDirection(Array.from(available)[0]);
    }

    setCurrentWord(word);
    setPlacementMode(true);
    setNewWord('');
    calculateValidPlacements(word, currentDirection);
  };

  const cancelPlacement = () => {
    setPlacementMode(false);
    setCurrentWord('');
    setSelectedCell(null);
    setValidPlacements(new Set());
    setPartialPlacements(new Set());
  };

  const handleDirectionChange = (newDirection: WordPosition['direction']) => {
    setCurrentDirection(newDirection);
    if (placementMode && currentWord) {
      calculateValidPlacements(currentWord, newDirection);
    }
  };

  const getCellHighlight = (row: number, col: number): string => {
    const cellKey = `${row}-${col}`;

    // In placement mode, show valid placement positions
    if (placementMode) {
      if (validPlacements.has(cellKey)) {
        return '#a5d6a7'; // Light green for valid placements (no overlap)
      }
      if (partialPlacements.has(cellKey)) {
        return '#fff59d'; // Light yellow for valid placements (with overlap)
      }
    }

    // Check if cell is part of any word
    for (const wordPos of wordPositions) {
      const [dx, dy] = getDirectionVector(wordPos.direction);
      const strippedWord = stripWordForGrid(wordPos.word);
      const normalizedWord = strippedWord.split('').map(normalizeHebrewLetter).join('');

      for (let i = 0; i < normalizedWord.length; i++) {
        if (wordPos.startX + (dx * i) === row && wordPos.startY + (dy * i) === col) {
          return '#c7edff'; // Light blue for word cells
        }
      }
    }

    if (selectedCell && selectedCell.x === row && selectedCell.y === col) {
      return '#ffeb3b'; // Yellow for selected cell
    }

    return 'white';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Grid Size Controls */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: '600' }}>Width:</label>
          <input
            type="number"
            value={gridWidth}
            onChange={(e) => {
              const newWidth = Math.max(4, Math.min(12, parseInt(e.target.value) || 8));
              initializeGrid(newWidth, gridHeight);
            }}
            min="4"
            max="12"
            style={{ width: '60px', padding: '0.25rem' }}
          />
        </div>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: '600' }}>Height:</label>
          <input
            type="number"
            value={gridHeight}
            onChange={(e) => {
              const newHeight = Math.max(4, Math.min(12, parseInt(e.target.value) || 8));
              initializeGrid(gridWidth, newHeight);
            }}
            min="4"
            max="12"
            style={{ width: '60px', padding: '0.25rem' }}
          />
        </div>
        <button
          type="button"
          onClick={fillEmptyCells}
          className="btn btn-secondary btn-small"
          style={{ marginLeft: 'auto' }}
        >
          <RotateCw size={16} />
          Fill Empty with Random Letters
        </button>
        <button
          type="button"
          onClick={clearRandomLetters}
          className="btn btn-secondary btn-small"
          title="Remove random letters, keep only placed words"
        >
          <Trash2 size={16} />
          Clear Random Letters
        </button>
        <button
          type="button"
          onClick={clearGrid}
          className="btn btn-danger btn-small"
        >
          <Trash2 size={16} />
          Clear Grid
        </button>
      </div>

      {/* Word Placement Controls */}
      <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Add Words to Grid</h3>

        {suggestedWords && (
          <div style={{ backgroundColor: '#f0f9ff', padding: '1rem', borderRadius: '8px', border: '1px solid #bae6fd', marginBottom: '1rem' }}>
            <label style={{ color: '#0369a1', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
              <Wand2 size={16} /> AI Suggested Words
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {suggestedWords.split(',').map(w => w.trim()).filter(w => w.length > 0).map((word, idx) => {
                const isAdded = wordPositions.some(wp => wp.word === word);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      if (!isAdded) setNewWord(word);
                    }}
                    disabled={isAdded}
                    style={{
                      background: isAdded ? '#e2e8f0' : 'white',
                      border: `1px solid ${isAdded ? '#cbd5e1' : '#3b82f6'}`,
                      color: isAdded ? '#94a3b8' : '#2563eb',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '16px',
                      fontSize: '0.85rem',
                      cursor: isAdded ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    {word} {isAdded && <Check size={12} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!placementMode ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={newWord}
              onChange={(e) => {
                const word = e.target.value;
                setNewWord(word);
                // Don't calculate directions on every keystroke - only when placing word
              }}
              placeholder="Enter word to place"
              style={{ flex: 1, minWidth: '200px', padding: '0.5rem' }}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), startPlacingWord())}
            />
            <select
              value={currentDirection}
              onChange={(e) => handleDirectionChange(e.target.value as WordPosition['direction'])}
              style={{ padding: '0.5rem' }}
              disabled={!newWord.trim()}
            >
              <option value="horizontal-reverse" disabled={!availableDirections.has('horizontal-reverse')}>
                Horizontal ← (Right to Left) {overlapDirections.has('horizontal-reverse') ? '⚠️' : ''}
              </option>
              <option value="vertical" disabled={!availableDirections.has('vertical')}>
                Vertical ↓ {overlapDirections.has('vertical') ? '⚠️' : ''}
              </option>
              <option value="vertical-up" disabled={!availableDirections.has('vertical-up')}>
                Vertical ↑ {overlapDirections.has('vertical-up') ? '⚠️' : ''}
              </option>
              <option value="diagonal-down-left" disabled={!availableDirections.has('diagonal-down-left')}>
                Diagonal ↙ {overlapDirections.has('diagonal-down-left') ? '⚠️' : ''}
              </option>
              <option value="diagonal-up-left" disabled={!availableDirections.has('diagonal-up-left')}>
                Diagonal ↖ {overlapDirections.has('diagonal-up-left') ? '⚠️' : ''}
              </option>
            </select>
            <button
              type="button"
              onClick={startPlacingWord}
              className="btn btn-primary btn-small"
            >
              <Plus size={16} />
              Place Word
            </button>
          </div>
        ) : (
          <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>
              📍 Placing: "{currentWord}"
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600' }}>Direction:</label>
              <select
                value={currentDirection}
                onChange={(e) => {
                  const newDir = e.target.value as WordPosition['direction'];
                  setCurrentDirection(newDir);
                  calculateValidPlacements(currentWord, newDir);
                }}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.9rem' }}
              >
                <option value="horizontal-reverse">Horizontal ← (Right to Left)</option>
                <option value="vertical">Vertical ↓</option>
                <option value="vertical-up">Vertical ↑</option>
                <option value="diagonal-down-left">Diagonal ↙</option>
                <option value="diagonal-up-left">Diagonal ↖</option>
              </select>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
              Click on a highlighted cell in the grid below to place the word starting from that position.
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#a5d6a7', border: '1px solid #999' }}></div>
                <span>Empty cells</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#fff59d', border: '1px solid #999' }}></div>
                <span>With overlap</span>
              </div>
            </div>
            <button
              type="button"
              onClick={cancelPlacement}
              className="btn btn-secondary btn-small"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Placed Words List */}
        {wordPositions.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ marginBottom: '0.5rem' }}>Placed Words ({wordPositions.length}):</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {wordPositions.map((wp, index) => {
                const isIncorrect = isIncorrectDirection(wp.direction);
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: isIncorrect ? '#fff59d' : 'white',
                      borderRadius: '4px',
                      border: isIncorrect ? '2px solid #fbc02d' : '1px solid #ddd'
                    }}
                  >
                    <span style={{ fontWeight: '600' }}>{wp.word}</span>
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>
                      ({wp.startX},{wp.startY}) {wp.direction}
                    </span>
                    {isIncorrect && (
                      <span style={{ fontSize: '0.8rem', color: '#f57c00', fontWeight: 'bold' }}>
                        ⚠️ Incorrect
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => repositionWord(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2196f3',
                        cursor: 'pointer',
                        padding: '0 0.25rem',
                        fontSize: '0.9rem'
                      }}
                      title="Reposition word"
                    >
                      ↻
                    </button>
                    <button
                      type="button"
                      onClick={() => removeWord(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        padding: '0 0.25rem'
                      }}
                      title="Remove word"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Grid Display */}
      <div style={{ overflowX: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridWidth}, 50px)`,
            gap: '2px',
            backgroundColor: '#ddd',
            padding: '2px',
            borderRadius: '4px',
            width: 'fit-content'
          }}
        >
          {gridData.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <input
                key={`${rowIndex}-${colIndex}`}
                type="text"
                value={cell}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value.slice(-1))}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                maxLength={1}
                style={{
                  width: '50px',
                  height: '50px',
                  textAlign: 'center',
                  fontSize: '20px',
                  fontWeight: '600',
                  border: '1px solid #ccc',
                  backgroundColor: getCellHighlight(rowIndex, colIndex),
                  cursor: placementMode ? 'pointer' : 'text'
                }}
                readOnly={placementMode}
              />
            ))
          )}
        </div>
      </div>

      {wordPositions.length === 0 && (
        <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', textAlign: 'center' }}>
          ⚠️ No words placed yet. Add words using the controls above, then click on the grid to place them.
        </div>
      )}
    </div>
  );
};

export default WordSearchGridEditor;
